/**
 * The record schema: the shape one analysed document is normalised into by
 * the record check, and therefore the columns of the Datensätze tab and of
 * the Excel file.
 *
 * Stored on the template as `recordSchema` (JSONB, free-form as far as the
 * gateway is concerned). A template without one uses the built-in contact
 * record — `CONTACT_SCHEMA` in `src/core/recordcheck.py`, mirrored by
 * `contactDefaultSchema()` below.
 *
 * Only the column *names and types* are editable here. `instructions` is the
 * model's system prompt for this schema; it is carried through untouched
 * when a schema already has one, because rewriting a prompt through a
 * settings form is a different job with different stakes.
 */

/** The checks the record check runs after the model pass. Anything else is
 *  a plain text column. Mirrors `RecordColumn.types` in the Flutter app and
 *  the deterministic checks in `src/core/recordcheck.py`. */
export const RECORD_COLUMN_TYPES = [
  'email',
  'phone',
  'postcode',
  'country',
  'url',
  'date',
] as const

export type RecordColumnType = (typeof RECORD_COLUMN_TYPES)[number]

/** German names for the checks, so the editor is not a list of English
 *  tokens. */
export const RECORD_COLUMN_TYPE_LABELS: Record<RecordColumnType, string> = {
  email: 'E-Mail-Adresse',
  phone: 'Telefonnummer',
  postcode: 'Postleitzahl',
  country: 'Land',
  url: 'Web-Adresse',
  date: 'Datum',
}

export interface RecordColumn {
  name: string
  description?: string
  type?: RecordColumnType | string
}

export interface RecordSchema {
  name: string
  columns: RecordColumn[]
  /** The model instructions of a schema that already carries some. Never
   *  edited here, always written back unchanged. */
  instructions?: string
}

/**
 * The built-in contact record, kept in step with `CONTACT_SCHEMA` in
 * `src/core/recordcheck.py`. Shown (marked "Standard") for a template that
 * has none of its own, and used to seed the editor.
 */
export function contactDefaultSchema(): RecordSchema {
  return {
    name: 'contact',
    columns: [
      { name: 'anrede' },
      { name: 'vorname' },
      { name: 'nachname' },
      { name: 'funktion' },
      { name: 'medium' },
      { name: 'institution' },
      { name: 'email', type: 'email' },
      { name: 'telefon', type: 'phone' },
      { name: 'strasse' },
      { name: 'plz', type: 'postcode' },
      { name: 'ort' },
      { name: 'land', type: 'country' },
      { name: 'website', type: 'url' },
      { name: 'sprache' },
      { name: 'datum', type: 'date' },
    ],
  }
}

/**
 * Reads whatever is stored. The column is free-form JSONB written by a
 * Python worker and by earlier versions of this app, so nothing about its
 * shape is guaranteed — a malformed schema must render as "keines" and let
 * the person fix it, not throw inside a settings tab.
 */
export function parseRecordSchema(stored: unknown): RecordSchema | null {
  if (stored == null || typeof stored !== 'object' || Array.isArray(stored)) return null
  const raw = stored as Record<string, unknown>
  const rawColumns = Array.isArray(raw.columns) ? raw.columns : []
  const columns: RecordColumn[] = []
  for (const entry of rawColumns) {
    if (entry == null || typeof entry !== 'object' || Array.isArray(entry)) continue
    const column = entry as Record<string, unknown>
    const name = typeof column.name === 'string' ? column.name.trim() : ''
    if (name === '') continue
    columns.push({
      name,
      ...(typeof column.description === 'string' && column.description.trim() !== ''
        ? { description: column.description.trim() }
        : {}),
      ...(typeof column.type === 'string' && column.type.trim() !== ''
        ? { type: column.type.trim() }
        : {}),
    })
  }
  if (columns.length === 0) return null
  return {
    name: typeof raw.name === 'string' && raw.name.trim() !== '' ? raw.name.trim() : 'contact',
    columns,
    ...(typeof raw.instructions === 'string' ? { instructions: raw.instructions } : {}),
  }
}

/** What goes back into `recordSchema`. Empty values are omitted rather than
 *  written as "", which the record check would read as a type it does not
 *  know. */
export function serializeRecordSchema(schema: RecordSchema): Record<string, unknown> {
  return {
    name: schema.name.trim() === '' ? 'contact' : schema.name.trim(),
    ...(schema.instructions != null ? { instructions: schema.instructions } : {}),
    columns: schema.columns
      .map((column) => ({
        name: column.name.trim(),
        ...(column.description?.trim() ? { description: column.description.trim() } : {}),
        ...(column.type?.trim() ? { type: column.type.trim() } : {}),
      }))
      .filter((column) => column.name !== ''),
  }
}

export type SchemaProblem = 'empty' | 'duplicate' | 'nameless'

/**
 * Why a draft cannot be saved.
 *
 * `empty` is not a style rule: a schema with no columns would serialise to
 * an empty list, and **the gateway ignores a `recordSchema` that is null or
 * absent** (`UpdateTemplateDocument` only forwards a non-nil map), so
 * "delete every column" would look like a save and change nothing. Refusing
 * it in the editor is the honest form of that limit.
 *
 * Duplicate names would collide as export columns; a nameless column would
 * be dropped silently by `serializeRecordSchema`.
 */
export function schemaProblem(schema: RecordSchema): SchemaProblem | null {
  const named = schema.columns.filter((column) => column.name.trim() !== '')
  if (named.length === 0) return 'empty'
  if (named.length !== schema.columns.length) return 'nameless'
  const seen = new Set<string>()
  for (const column of named) {
    const key = column.name.trim().toLocaleLowerCase('de-DE')
    if (seen.has(key)) return 'duplicate'
    seen.add(key)
  }
  return null
}

export const SCHEMA_PROBLEM_MESSAGES: Record<SchemaProblem, string> = {
  empty:
    'Mindestens eine Spalte wird gebraucht. Ein leeres Schema lässt sich nicht speichern — der Server behält dann das bisherige.',
  nameless: 'Jede Spalte braucht einen Namen.',
  duplicate: 'Zwei Spalten haben denselben Namen. Namen werden zu Spalten in der Excel-Datei.',
}
